import { createRoot, hydrateRoot } from 'react-dom/client'
import { version as reactVersion } from 'react'
import type { ReactElement } from 'react'
import { loadableReady } from '@loadable/component'

import type { RailsContextInterface } from '../contexts/RailsContext'
import { dispatchRollbarError } from '../utils/rollbar'

type RecoverableErrorInfo = { componentStack?: string; digest?: string }

// Pre-hydration HTML snapshot, keyed by container so it GC's with the element.
// Used to capture an SSR-vs-client byte diff when onRecoverableError fires.
const snapshots = new WeakMap<Element, string>()

// One snapshot per container per session — a single hydration cascade can fire
// onRecoverableError dozens of times against the same container as React
// re-renders the failing tree, and every repeat produces the same diff.
const snapshotted = new WeakSet<Element>()

const HYDRATION_DIFF_SAMPLE_RATE = 1
// 200 chars each side of a divergent byte. Long enough to grep the bundled
// source for the markup; short enough that up to three windows still fit in
// a Rollbar payload.
const HYDRATION_DIFF_CONTEXT = 200
// Labels for each divergence window in the Rollbar payload. The array length
// also caps how many distinct divergences we capture per snapshot.
const WINDOW_LABELS = ['first', 'second', 'third'] as const
const HYDRATION_DIFF_MAX_WINDOWS = WINDOW_LABELS.length
// After capturing a divergence, look for a re-sync point in the client string
// using a probe of this length so a length-shifting diff doesn't cascade into
// false positives. RESYNC_LOOKAHEAD bounds the search range.
const HYDRATION_DIFF_RESYNC_PROBE = 20
const HYDRATION_DIFF_RESYNC_LOOKAHEAD = 2000

// Allowlist of attribute values to keep through redaction — className is the
// primary grep target for locating mismatching components; the rest tell
// otherwise-identical tag shapes apart.
const STRUCTURAL_ATTR_NAMES = new Set([
  'class',
  'role',
  'type',
  'data-testid',
  'data-component',
  'aria-hidden',
  'dir',
  'lang',
])

// Scrub rendered user data before shipping the diff to Rollbar — Rollbar
// payload extras are not server-side filtered. Attributes are sorted by name
// per tag so a render that emits the same attribute set in a different
// serialised order doesn't dominate the diff — React reconciles by attribute
// name, not by position.
const redactHtmlForDiff = (html: string): string =>
  html
    .replace(/<!--[\s\S]*?-->/g, '<!---->')
    .replace(
      /([a-zA-Z_:][-\w:.]*)=("[^"]*"|'[^']*')/g,
      (_match, name: string, value: string) =>
        STRUCTURAL_ATTR_NAMES.has(name.toLowerCase())
          ? `${name}=${value}`
          : `${name}=""`,
    )
    .replace(/>[^<]*</g, '><')
    .replace(
      /<([\w:-]+)((?:\s+[\w:-]+(?:=(?:"[^"]*"|'[^']*'))?)+)(\s*\/?>)/g,
      (_match, tag: string, attrsStr: string, close: string) => {
        // `String#match` with the /g flag (es5) returns the matched substrings,
        // which is the pair we want; avoiding `matchAll` keeps us inside the
        // tsconfig `lib` target (es6 + DOM) without relying on transitive
        // expansion from @types dependencies.
        const pairs = (
          attrsStr.match(/[\w:-]+(?:=(?:"[^"]*"|'[^']*'))?/g) ?? []
        ).sort()
        return `<${tag} ${pairs.join(' ')}${close}`
      },
    )
    // React 18 SSR text separators don't survive hydration on the client —
    // strip from both sides so the diff doesn't fire on React's own mechanism.
    .replace(/<!---->/g, '')

const sliceWindow = (s: string, at: number): string =>
  s.slice(Math.max(0, at - HYDRATION_DIFF_CONTEXT), at + HYDRATION_DIFF_CONTEXT)

type Divergence = { ssrAt: number; clientAt: number }

const findDivergences = (ssr: string, client: string): Divergence[] => {
  const out: Divergence[] = []
  let ssrI = 0
  let clientI = 0
  while (out.length < HYDRATION_DIFF_MAX_WINDOWS) {
    while (
      ssrI < ssr.length &&
      clientI < client.length &&
      ssr.charCodeAt(ssrI) === client.charCodeAt(clientI)
    ) {
      ssrI += 1
      clientI += 1
    }
    if (ssrI >= ssr.length && clientI >= client.length) break
    out.push({ ssrAt: ssrI, clientAt: clientI })
    // Advance past the window we just captured, then re-sync on a short probe
    // so a length-shifting diff doesn't cascade. Try ssr→client first (covers
    // client-extended diffs); fall back to client→ssr; give up if neither.
    ssrI += HYDRATION_DIFF_CONTEXT
    clientI += HYDRATION_DIFF_CONTEXT
    const ssrProbe = ssr.slice(ssrI, ssrI + HYDRATION_DIFF_RESYNC_PROBE)
    const clientProbe = client.slice(
      clientI,
      clientI + HYDRATION_DIFF_RESYNC_PROBE,
    )
    if (
      ssrProbe.length < HYDRATION_DIFF_RESYNC_PROBE ||
      clientProbe.length < HYDRATION_DIFF_RESYNC_PROBE
    )
      break
    // Slice the haystack to LOOKAHEAD + PROBE chars so `indexOf` can't scan
    // arbitrarily far past the lookahead limit before returning a discardable
    // match. PROBE is included so a match starting at exactly LOOKAHEAD fits.
    const haystackLen =
      HYDRATION_DIFF_RESYNC_LOOKAHEAD + HYDRATION_DIFF_RESYNC_PROBE
    const clientHaystack = client.slice(clientI, clientI + haystackLen)
    const clientRel = clientHaystack.indexOf(ssrProbe)
    if (clientRel !== -1) {
      clientI += clientRel
      // eslint-disable-next-line no-continue
      continue
    }
    const ssrHaystack = ssr.slice(ssrI, ssrI + haystackLen)
    const ssrRel = ssrHaystack.indexOf(clientProbe)
    if (ssrRel !== -1) {
      ssrI += ssrRel
      // eslint-disable-next-line no-continue
      continue
    }
    break
  }
  return out
}

const maybeReportHydrationDiff = (
  container: Element,
  i18nLocale: string | undefined,
  info: RecoverableErrorInfo,
): void => {
  if (snapshotted.has(container)) return
  if (Math.random() > HYDRATION_DIFF_SAMPLE_RATE) return
  const rawSsr = snapshots.get(container)
  if (!rawSsr) return
  const ssr = redactHtmlForDiff(rawSsr)
  const client = redactHtmlForDiff(container.innerHTML)
  if (ssr === client) return

  const divergences = findDivergences(ssr, client)
  if (divergences.length === 0) return

  const extras: Record<string, unknown> = {
    ssr_length: ssr.length,
    client_length: client.length,
    i18n_locale: i18nLocale,
    componentStack: info.componentStack,
    digest: info.digest,
  }
  divergences.forEach((d, i) => {
    const label = WINDOW_LABELS[i]
    extras[`ssr_${label}`] = sliceWindow(ssr, d.ssrAt)
    extras[`client_${label}`] = sliceWindow(client, d.clientAt)
    extras[`diff_offset_${label}_ssr`] = d.ssrAt
    extras[`diff_offset_${label}_client`] = d.clientAt
  })
  // Alias kept for back-compat with existing Rollbar saved queries.
  extras.diff_offset_first = divergences[0].ssrAt

  dispatchRollbarError(new Error('hydration_diff_snapshot'), extras)
  snapshotted.add(container)
}

const onRecoverableError =
  (container: Element | null, i18nLocale: string | undefined) =>
  (error: unknown, errorInfo: unknown): void => {
    const info = (errorInfo ?? {}) as RecoverableErrorInfo
    dispatchRollbarError(error as Error, {
      componentStack: info.componentStack,
      digest: info.digest,
      react_version: reactVersion,
      boundary: 'on-recoverable-error',
      i18n_locale: i18nLocale,
    })
    if (container) maybeReportHydrationDiff(container, i18nLocale, info)
  }

type Props = Record<string, unknown>
export type BuildElement = (
  props: Props,
  railsContext: RailsContextInterface,
) => ReactElement

// react-on-rails treats a registered factory with three parameters as an
// `isRenderer` and delegates mounting to us instead of calling hydrateRoot
// itself. Owning the call lets us pass onRecoverableError, which forwards
// React 18 hydration errors (#418/#423/#425) to Rollbar with componentStack
// and digest — window.onerror routes them today with neither, leaving the
// items unactionable.
//
// `react_component(..., prerender: false)` mounts (e.g. ShowMoreText in
// app/views/adventures/listing_adventures/_listing_adventure.html.slim)
// land on empty containers — hydrateRoot would treat that as a mismatch.
// Mirror react-on-rails' own `!!innerHTML` check to pick createRoot in
// that case.
// loadableReady() resolves after every chunk script tag emitted by
// ChunkExtractor in the SSR'd HTML has been downloaded and evaluated.
// Without this, hydrateRoot can fire before the lazy chunks land,
// causing React to render fallbacks against the server's real markup
// and mark the boundary as mismatched. When no chunks were declared
// (no loadable splits on the page, or no ChunkExtractor tags) it
// resolves immediately, so this is safe even on routes without splits.
//
// Note: loadableReady reads window.__LOADABLE_REQUIRED_CHUNKS___ which
// is set globally by the App renderer's chunk-tag script. Renderers
// without their own ChunkExtractor (e.g. Footer) inherit that wait
// when rendered on the same page as App, so they're effectively gated
// on App's lazy chunks. The delay is negligible in practice but worth
// knowing when reasoning about hydration ordering across renderers.
const createRenderer =
  (buildElement: BuildElement) =>
  (
    props: Props,
    railsContext: RailsContextInterface,
    domNodeId: string,
  ): void => {
    loadableReady(() => {
      const container = document.getElementById(domNodeId)
      if (!container) return
      const element = buildElement(props, railsContext)
      if (container.innerHTML) {
        snapshots.set(container, container.innerHTML)
        hydrateRoot(container, element, {
          onRecoverableError: onRecoverableError(
            container,
            railsContext.i18nLocale,
          ),
        })
      } else {
        createRoot(container, {
          onRecoverableError: onRecoverableError(null, railsContext.i18nLocale),
        }).render(element)
      }
    })
  }

export { onRecoverableError }
export default createRenderer
